/**
 * Client approvals (R191, decision 1010 item 2; docs/specs/portal/client-approvals.md; help `cloud.client-approvals`).
 *
 * - The client (a project's Approvals tab, and the client portal's Approvals page for every project and contract):
 *   each request with what it is, the version and its fingerprint, the operator's note; "Review" opens the document
 *   (the Safety version or the contract) or the block change's before and after on the map. A client admin approves,
 *   or declines with the reason; a contract is signed with DocuSeal. Client viewers read; Preview as client reads.
 * - Staff (a project's Approvals tab): the requests waiting, each survey block with "Send to client for approval" and
 *   its latest state (and whether the block changed since it was sent), and what was decided. The JSA, JSP and risk
 *   assessment are sent from the Safety tab, a contract from Contracts.
 *
 * The database decides who may do what (`approval_send`, `approval_decide`, `portal_approvals`); the role checks here
 * only choose which buttons to draw.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  APPROVAL_KIND_LABEL,
  approvalDecisionLine,
  approvalVersionText,
  canApprove,
  canSendApproval,
  declineReasonProblem,
  latestApproval,
  shortHash,
  sizeText,
  sortApprovals,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { TextArea } from '../components/Fields.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import type { PortalProject } from '../portal/api.ts';
import {
  approvalFileUrl,
  contractSigningUrl,
  decideApproval,
  loadBlockHashes,
  loadPortalApproval,
  loadPortalApprovals,
  loadProjectApprovals,
  refreshContractSigning,
  sendApproval,
  signedContractUrl,
  withdrawApproval,
  type PortalApproval,
  type PortalApprovalDetail,
  type StaffApproval,
} from './api.ts';
import { ApprovalChip, ApprovalLine, BlockChangeView, SendForApproval } from './ApprovalParts.tsx';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export interface ApprovalServices {
  /** Open an address in a new tab (tests record it). */
  readonly open?: (url: string) => void;
}

const defaultOpen = (url: string): void => {
  const tab = window.open(url, '_blank', 'noopener');
  if (!tab) window.location.assign(url);
};

// ------------------------------------------------------------------------------------------------ the client

/**
 * The client's requests: one project's (`projectId`), or every one of the organisation (the Approvals page, which
 * also holds contracts made before their project).
 */
export function ClientApprovals({
  sb,
  clientOrgId,
  projectId,
  clientRole,
  preview = false,
  services = {},
}: {
  readonly sb: SupabaseClient;
  readonly clientOrgId: string;
  readonly projectId: string | null;
  /** The client's role in the organisation (in a preview, the role previewed). */
  readonly clientRole: string | null;
  readonly preview?: boolean;
  readonly services?: ApprovalServices;
}): ReactElement {
  const [rows, setRows] = useState<PortalApproval[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const asAdmin = canApprove(clientRole);
  const reload = useCallback(() => {
    setError(null);
    loadPortalApprovals(sb, clientOrgId, projectId, asAdmin)
      .then((r) => setRows(sortApprovals(r)))
      .catch((e: unknown) => setError(message(e)));
  }, [sb, clientOrgId, projectId, asAdmin]);
  useEffect(reload, [reload]);

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!rows) return <Loading what="the approvals" />;
  const waiting = rows.filter((r) => r.state === 'waiting').length;
  return (
    <div className="stack">
      <p className="body">
        {asAdmin
          ? 'What the operator asks you to approve: survey block changes, the JSA, the JSP, the risk assessment and contracts to sign. Review each one, then approve it or decline it with your reason. Your name and the time are recorded with the version you saw.'
          : 'What the operator asked your organisation to approve and what was decided. A client admin of your organisation approves or declines.'}
      </p>
      {preview && (
        <p className="caption muted">
          Preview: read only. The client admin sees Approve, Decline and Sign here.
        </p>
      )}
      {rows.length === 0 ? (
        <p className="body muted">Nothing has been sent to you for approval.</p>
      ) : (
        <>
          <p className="caption muted" role="status">
            {waiting === 0 ? 'Nothing is waiting for you.' : `${waiting} waiting.`}
          </p>
          <ul className="stack" aria-label="Approvals">
            {rows.map((r) => (
              <ClientApprovalItem
                key={r.id}
                sb={sb}
                row={r}
                showProject={projectId === null}
                asAdmin={asAdmin}
                open={services.open ?? defaultOpen}
                onChanged={reload}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function ClientApprovalItem({
  sb,
  row,
  showProject,
  asAdmin,
  open,
  onChanged,
}: {
  readonly sb: SupabaseClient;
  readonly row: PortalApproval;
  readonly showProject: boolean;
  /** Preview as a client admin: read the requests a client admin reads. */
  readonly asAdmin: boolean;
  readonly open: (url: string) => void;
  readonly onChanged: () => void;
}): ReactElement {
  const [detail, setDetail] = useState<PortalApprovalDetail | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [mode, setMode] = useState<'approve' | 'decline' | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const label = APPROVAL_KIND_LABEL[row.kind];
  const version = approvalVersionText(row);
  const signing =
    row.kind === 'contract' && (row.signing_state === 'sending' || row.signing_state === 'sent');
  // decision 1014: once the client has signed, the operator's manager signs; nothing is left for the client to do
  const clientSigned = row.kind === 'contract' && !!row.client_signed_at;
  async function run(key: string, what: () => Promise<string | null>): Promise<void> {
    setBusy(key);
    setNotice(null);
    try {
      const ok = await what();
      if (ok) setNotice({ ok: true, text: ok });
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(null);
    }
  }

  const review = (): Promise<void> =>
    run('review', async () => {
      if (reviewing) {
        setReviewing(false);
        return null;
      }
      setDetail(detail ?? (await loadPortalApproval(sb, row.id, asAdmin)));
      setReviewing(true);
      return null;
    });

  const openFile = (): Promise<void> =>
    run('file', async () => {
      const d = detail ?? (await loadPortalApproval(sb, row.id, asAdmin));
      setDetail(d);
      open(await approvalFileUrl(sb, d));
      return null;
    });

  return (
    <li className="panel stack approval-item" aria-label={`${label}: ${row.title}, ${version}`}>
      <div className="row row--wrap">
        <div className="grow">
          <p className="heading">
            {row.title}
            {showProject && row.project_number ? ` · ${row.project_number} ${row.project_title}` : ''}
          </p>
          <p className="caption muted">
            {label}, {version.toLowerCase()} (fingerprint{' '}
            <span className="data">{shortHash(row.version_hash)}</span>)
            {row.file_name ? ` · ${row.file_name}` : ''}
            {row.size_bytes ? `, ${sizeText(row.size_bytes)}` : ''}
          </p>
        </div>
        <ApprovalChip row={row} reader="client" />
      </div>
      {row.note && <p className="body approval-item__note">{row.note}</p>}
      <p className="caption">{approvalDecisionLine(row)}</p>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      <div className="row row--wrap">
        {row.kind === 'block_change' ? (
          <Button
            variant="quiet"
            busy={busy === 'review'}
            onClick={() => void review()}
            aria-expanded={reviewing}
          >
            {reviewing ? 'Hide the change' : 'Review the change on the map'}
          </Button>
        ) : (
          <Button variant="quiet" busy={busy === 'file'} onClick={() => void openFile()}>
            Open{' '}
            {row.kind === 'contract'
              ? 'the contract'
              : `the ${label === 'Risk assessment' ? 'risk assessment' : label}`}
          </Button>
        )}
        {row.kind === 'contract' && row.signed_available && (
          <Button
            variant="quiet"
            busy={busy === 'signed'}
            onClick={() =>
              void run('signed', async () => {
                open(await signedContractUrl(sb, row));
                return null;
              })
            }
          >
            Download the signed copy
          </Button>
        )}
      </div>
      {reviewing && detail?.snapshot && (
        <BlockChangeView after={detail.snapshot} before={detail.before_snapshot} />
      )}
      {row.can_act && clientSigned && (
        <p className="caption muted">
          You have signed. {row.manager_name || 'The operator’s manager'} signs next for the operator; then
          the contract shows here as signed by both, with the signed copy.
        </p>
      )}
      {row.can_act && row.kind === 'contract' && !clientSigned && (
        <div className="row row--wrap">
          <Button
            variant="primary"
            busy={busy === 'sign'}
            onClick={() =>
              void run('sign', async () => {
                open(await contractSigningUrl(sb, row.id));
                return 'DocuSeal opened in a new tab. Once you have signed, the contract shows here as signed.';
              })
            }
          >
            Sign with DocuSeal
          </Button>
          {signing && (
            <Button
              variant="quiet"
              busy={busy === 'check'}
              onClick={() =>
                void run('check', async () => {
                  await refreshContractSigning(sb, row.id);
                  onChanged();
                  return null;
                })
              }
            >
              Check DocuSeal now
            </Button>
          )}
          {!signing && mode !== 'decline' && (
            <Button variant="quiet" onClick={() => setMode('decline')}>
              Decline
            </Button>
          )}
          {signing && (
            <span className="caption muted">
              To decline, choose Decline in DocuSeal and give your reason.
            </span>
          )}
        </div>
      )}
      {row.can_act && row.kind !== 'contract' && mode === null && (
        <div className="row row--wrap">
          <Button variant="primary" onClick={() => setMode('approve')}>
            Approve
          </Button>
          <Button variant="secondary" onClick={() => setMode('decline')}>
            Decline
          </Button>
        </div>
      )}
      {mode === 'approve' && (
        <div className="stack" role="group" aria-label="Approve">
          <p className="body">
            You approve {row.title}, {version.toLowerCase()} (fingerprint{' '}
            <span className="data">{shortHash(row.version_hash)}</span>), as it was sent. Your name and the
            time are recorded.
          </p>
          <div className="row">
            <Button
              variant="primary"
              busy={busy === 'approve'}
              onClick={() =>
                void run('approve', async () => {
                  await decideApproval(sb, row.id, true, '');
                  setMode(null);
                  onChanged();
                  return null;
                })
              }
            >
              Approve
            </Button>
            <Button variant="quiet" onClick={() => setMode(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {mode === 'decline' && (
        <form
          className="stack"
          aria-label="Decline"
          onSubmit={(e) => {
            e.preventDefault();
            const problem = declineReasonProblem(reason);
            if (problem) {
              setNotice({ ok: false, text: problem });
              return;
            }
            void run('decline', async () => {
              await decideApproval(sb, row.id, false, reason.trim());
              setMode(null);
              onChanged();
              return null;
            });
          }}
        >
          <TextArea
            label="Why you decline"
            rows={3}
            value={reason}
            onChange={setReason}
            hint="The operator reads it and sends a changed version."
          />
          <div className="row">
            <Button type="submit" variant="primary" busy={busy === 'decline'}>
              Decline
            </Button>
            <Button variant="quiet" onClick={() => setMode(null)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </li>
  );
}

// ------------------------------------------------------------------------------------------------ staff

/** A project's Approvals tab for staff: what waits, each survey block with its state and Send, and what was decided. */
export function StaffApprovals({
  sb,
  project,
  roles,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly roles: readonly string[];
}): ReactElement {
  const [rows, setRows] = useState<StaffApproval[] | null>(null);
  const [blocks, setBlocks] = useState<{ id: string; name: string; digit: string }[]>([]);
  const [hashes, setHashes] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [shown, setShown] = useState<string | null>(null);

  const reload = useCallback(() => {
    setError(null);
    Promise.all([
      loadProjectApprovals(sb, project.id),
      Promise.resolve(
        sb
          .from('block')
          .select('id, name, digit, kind, deleted_at')
          .eq('project_id', project.id)
          .order('digit'),
      ).then((r) => {
        if (r.error) throw new Error(r.error.message);
        return (
          (r.data ?? []) as {
            id: string;
            name: string;
            digit: string;
            kind: string;
            deleted_at: string | null;
          }[]
        ).filter((b) => b.kind === 'survey' && b.deleted_at === null);
      }),
      loadBlockHashes(sb, project.id).catch(() => new Map<string, string>()),
    ])
      .then(([r, b, h]) => {
        setRows(r);
        setBlocks(b);
        setHashes(h);
      })
      .catch((e: unknown) => setError(message(e)));
  }, [sb, project.id]);
  useEffect(reload, [reload]);

  async function run(key: string, what: () => Promise<void>, ok: string): Promise<void> {
    setBusy(key);
    setNotice(null);
    try {
      await what();
      setNotice({ ok: true, text: ok });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(null);
    }
  }

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!rows) return <Loading what="the approvals" />;
  const mayBlock = canSendApproval(roles, 'block_change');
  const blocked = !project.clientOrgId
    ? 'Link a client organisation to this project first.'
    : project.status === 'archived'
      ? 'The project is archived.'
      : null;
  const waiting = rows.filter((r) => r.state === 'waiting');
  const decided = rows.filter((r) => r.state !== 'waiting');

  const item = (r: StaffApproval): ReactElement => (
    <li key={r.id} className="panel stack approval-item" aria-label={`${r.title}, ${approvalVersionText(r)}`}>
      <div className="row row--wrap">
        <div className="grow">
          <p className="body">
            {r.title} · {APPROVAL_KIND_LABEL[r.kind]}, {approvalVersionText(r).toLowerCase()}
          </p>
          <ApprovalLine
            row={r}
            changed={r.kind === 'block_change' && hashes.get(r.item_id) !== r.version_hash}
          />
          {r.note && <p className="caption muted approval-item__note">Note sent: {r.note}</p>}
        </div>
        {r.kind === 'block_change' && r.snapshot && (
          <Button
            variant="quiet"
            onClick={() => setShown(shown === r.id ? null : r.id)}
            aria-expanded={shown === r.id}
          >
            {shown === r.id ? 'Hide the change' : 'Show the change'}
          </Button>
        )}
        {r.state === 'waiting' && canSendApproval(roles, r.kind) && (
          <Button
            variant="quiet"
            busy={busy === `withdraw:${r.id}`}
            onClick={() =>
              void run(
                `withdraw:${r.id}`,
                () => withdrawApproval(sb, r),
                `${r.title} was withdrawn from the client.`,
              )
            }
            aria-label={`Withdraw ${r.title}, ${approvalVersionText(r).toLowerCase()}`}
          >
            Withdraw
          </Button>
        )}
      </div>
      {shown === r.id && r.snapshot && <BlockChangeView after={r.snapshot} before={r.before_snapshot} />}
    </li>
  );

  return (
    <div className="stack">
      <p className="body">
        What this project&apos;s client is asked to approve. Send a survey block change here; send the JSA,
        the JSP and the risk assessment from the Safety tab and a contract from Contracts. A client admin
        approves or declines with a reason; a newer version sent replaces one still waiting.
        {!project.clientOrgId &&
          ' No client organisation is linked to this project, so nothing can be sent yet.'}
      </p>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      <section className="panel stack" aria-label="Waiting for the client">
        <h2 className="heading">Waiting for the client ({waiting.length})</h2>
        {waiting.length === 0 ? (
          <p className="body muted">Nothing is waiting for the client.</p>
        ) : (
          <ul className="stack">{sortApprovals(waiting).map(item)}</ul>
        )}
      </section>
      <section className="panel stack" aria-label="Survey blocks">
        <h2 className="heading">Survey blocks</h2>
        <p className="caption muted">
          The block&apos;s boundary and planned lines as Teslyn Field last synced them. The client sees them
          on the map beside the version they last approved.
        </p>
        {blocks.length === 0 && <p className="body muted">No survey block has reached Teslyn Cloud yet.</p>}
        {blocks.map((b) => {
          const latest = latestApproval(rows, b.id);
          return (
            <div key={b.id} className="row row--wrap">
              <div className="grow">
                <p className="body">
                  Block {b.digit} {b.name}
                </p>
                {latest ? (
                  <ApprovalLine row={latest} changed={hashes.get(b.id) !== latest.version_hash} />
                ) : (
                  <p className="caption muted">Not sent to the client yet.</p>
                )}
              </div>
              {mayBlock && (
                <SendForApproval
                  what={`block ${b.name}`}
                  disabledReason={blocked}
                  busy={busy === `send:${b.id}`}
                  onSend={(note) =>
                    void run(
                      `send:${b.id}`,
                      async () => {
                        await sendApproval(sb, { kind: 'block_change', itemId: b.id, note });
                      },
                      `Block ${b.name} was sent to the client for approval.`,
                    )
                  }
                />
              )}
            </div>
          );
        })}
      </section>
      <section className="panel stack" aria-label="Decided and replaced">
        <h2 className="heading">Decided and replaced ({decided.length})</h2>
        {decided.length === 0 ? (
          <p className="body muted">Nothing decided yet.</p>
        ) : (
          <ul className="stack">{decided.map(item)}</ul>
        )}
      </section>
    </div>
  );
}

/** A project's Approvals tab: staff or the client. */
export function ApprovalsTab({
  sb,
  project,
  mode,
  roles = [],
  clientRole = null,
  preview = false,
  services = {},
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly mode: 'client' | 'staff';
  readonly roles?: readonly string[];
  readonly clientRole?: string | null;
  readonly preview?: boolean;
  readonly services?: ApprovalServices;
}): ReactElement {
  if (mode === 'staff') return <StaffApprovals sb={sb} project={project} roles={roles} />;
  if (!project.clientOrgId) return <p className="body muted">Nothing has been sent to you for approval.</p>;
  return (
    <ClientApprovals
      sb={sb}
      clientOrgId={project.clientOrgId}
      projectId={project.id}
      clientRole={clientRole}
      preview={preview}
      services={services}
    />
  );
}
